import { NextApiRequest, NextApiResponse } from 'next'
import { serialize, CookieSerializeOptions } from 'cookie'
import { __application_environment } from './utils'

const ApiUtils = {
  Endpoints: {
    guest: {
      source: '/v1/auth/guest-user',
    },
    oauth(oauthType: string) {
      return {
        source: `/v1/auth/${oauthType}/`,
        mirror: '/api/oauth',
      }
    },
    login: {
      source: '/v1/auth/login/',
      mirror: '/api/auth/login',
    },
    signup: {
      source: 'v1/auth/register/',
      mirror: '/api/auth/signup',
    },
    featured: {
      source: '/v4/comic?segment=featured',
      mirror: '/api/comics/featured',
    },
    featured_origins: {
      source: '/v4/comic?segment=featured_originals',
      mirror: '/api/origins/featured',
    },
    origins: {
      source: '/v4/comic?segment=originals', // allows pagination
      mirror: '/api/origins',
    },
    latest_comics: {
      source: '/v4/comic/?latest=1', // allows pagination
      mirror: '/api/comics/latest',
    },
    search: {
      source: '/v1/comic/search',
      mirror: '/api/comics/search',
    },
    comics: {
      source: '/v4/comic',
      mirror: '/api/comics',
    },
    genres: {
      list: {
        source: '/v1/comic/genres',
        mirror: '/api/genres',
      },
      comics(params: { gid: string }) {
        return {
          source: `/v4/comic?genre=${params.gid}`,
          mirror: `/api/genres/${params.gid}`,
        }
      },
    },
    comics_single: {
      source: '/v2/comic/web-episodes/',
      mirror: '/api/comics',
    },
    comics_episode_purchase: {
      source: '/v2/comic/purchase/episode/',
      mirror: '/api/shop/purchases/episode',
    },
    comics_episode(params: { comicId?: string; episodeId?: string }) {
      return {
        source: `/v2/comic/web-episode/${params?.comicId}/${params?.episodeId}/`,
        mirror: `/api/episodes/${params?.episodeId}?cid=${params?.comicId}`,
      }
    },
    comments(params: { episodeId?: string }) {
      return {
        source: `/v1/comic/comments/${params.episodeId}`,
        mirror: `/api/episodes/${params?.episodeId}/comments`,
      }
    },
    comment_actions: {
      like(params: { episodeId?: string; commentId?: string }) {
        return {
          source: '/v1/comic/episode/2/like/',
          mirror: `/api/episodes/${params.episodeId}/comments/${params.commentId}/like`,
        }
      },
      post(params: { episodeId?: string }) {
        return {
          source: '/v1/comic/comment/',
          mirror: `/api/episodes/${params.episodeId}/comment`,
        }
      },
    },
    slices: {
      source: '/v4/comic/?segment=slices', // allows pagination
      mirror: '/api/slices',
    },
    shop: {
      bundle: {
        source: '/v1/payments/products',
        mirror: '/api/shop',
      },
      checkout(params?: { paymentMethod?: string; productId?: string }) {
        return {
          source: `/v1/payments/${params?.paymentMethod}/buy-product/${params?.productId}/`,
          mirror: '/api/shop/purchases/coins',
        }
      },
    },
    activities: {
      bookmarks: {
        source: '/v1/comic/comic-bookmarks', // allows pagination
        mirror: '/api/activities/bookmarks',
      },
      bookmark_single: {
        source: '/v1/comic/comic-bookmark/',
        mirror: '/api/activities/bookmarks/',
      },
      recent: {
        source: '/v1/profile/recent-comics/',
        mirror: '/api/activities/recent',
      },
      following: {
        source: '/v1/profile/subscribed-comics',
        mirror: '/api/activities/following',
      },
      follow: {
        source: (id: string) => `/v1/subscription/comics/${id}/`,
        mirror: '/api/activities/follow/',
      },
      comments: {
        source: '/v1/profile/comments',
        mirror: '/api/activities/comments',
      },
      comics: {
        upvote(comicId: string) {
          return {
            source: `/v2/comic/comic/${comicId}/like/`,
            mirror: '/api/activities/comics/upvote',
          }
        },
        downVote(comicId: string) {
          return {
            source: `/v2/comic/comic/${comicId}/unlike/`,
            mirror: '/api/activities/comics/downvote',
          }
        },
      },
      episodes: {
        upvote(episodeId: string) {
          return {
            source: `/v1/comic/episode/${episodeId}/like/`,
            mirror: '/api/activities/episodes/upvote',
          }
        },
        downVote(episodeId: string) {
          return {
            source: `/v1/comic/episode/${episodeId}/unlike/`,
            mirror: '/api/activities/episodes/downvote',
          }
        },
      },
      comment_action: {
        upvote(params: { commentId: string }) {
          return {
            source: `/v1/comic/comment/${params?.commentId}/like/`,
            mirror: '/api/activities/comments/upvote',
          }
        },
        downvote(params: { commentId?: string }) {
          return {
            source: `/v1/comic/comment/${params?.commentId}/unlike/`,
            mirror: '/api/activities/comments/downvote',
          }
        },
      },
    },
    account: {
      balance: {
        source: '/v1/coin/balance',
        mirror: '/api/account/balance',
      },
      index: {
        source: '/v1/profile',
        mirror: '/api/account',
      },
      logout: {
        source: '',
        mirror: '/api/account/logout',
      },
      delete: {
        source: '/v1/auth/delete-account/',
        mirror: '/api/account/delete',
      },
    },
    artists(id: string) {
      return {
        profile: {
          source: `/v1/comic/author/${id}`,
          mirror: `/api/artists/${id}/profile`,
        },

        tip: {
          source: '/v1/payments/tip-artist/',
          mirror: `/api/artists/${id}/tip`,
        },
      }
    },
  },

  ApiResponse: class ApiResponse {
    constructor(
      private message: string,
      private statusCode: number = 400,
      private data?: Record<string, any>
    ) {}
  },

  CookieOptionsDefault(expires?: Date): CookieSerializeOptions {
    return {
      sameSite: 'strict',
      httpOnly: true,
      path: '/',
      secure: __application_environment === 'Production',
      expires,
    }
  },

  SetCookie(
    res: NextApiResponse,
    name: string,
    value: unknown,
    options: CookieSerializeOptions = {}
  ) {
    const stringValue =
      typeof value === 'object' ? 'j:' + JSON.stringify(value) : String(value)

    if (typeof options.maxAge === 'number') {
      options.expires = new Date(Date.now() + options.maxAge * 1000)
    }
    res.setHeader('Set-Cookie', serialize(name, stringValue, options))
  },

  async withResponse(
    request: Promise<{ result?: Record<string, any>; error?: Error }>,
    nextRequest: NextApiRequest,
    nextResponse: NextApiResponse,
    options?: {
      message?: string
      serializer?: (param: any) => any
    }
  ) {
    const { result, error } = await request
    if (error) {
      return nextResponse
        .status(400)
        .json(new ApiUtils.ApiResponse(error.message))
    }
    nextResponse
      .status(200)
      .json(
        new ApiUtils.ApiResponse(
          options?.message || 'Action completed',
          200,
          options?.serializer ? options.serializer(result) : result
        )
      )
  },

  ComicsSerializer(result: any, page?: string) {
    const nextPage = parseInt(page || '1') + 1
    return {
      meta: {
        pages: result.number_of_pages,
        total: result.count,
        next:
          result?.number_of_pages > 1
            ? nextPage > result.number_of_pages
              ? null
              : nextPage
            : null,
      },
      comics: result.data,
    }
  },
}

export default ApiUtils
